import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { DateTime } from 'luxon';

import type {
  GetImportMBFileResponse,
  ImportFileRequirementRow,
  ImportMBFileUploadOptions,
} from '@appTypes/esb';
import { convertCSVColumnstoFile } from '@shared/utils';

import { isCampTerminal } from './constants';
import type { CampImportStatus } from './hooks';
import type {
  CampSummary,
  ImportButtonState,
  ImportErrorsCsvColumns,
} from './types';

/** Deliberate: the upload always sends `import_<date>.txt`, not the user's filename */
export const buildVendorFormData = (file: File): FormData => {
  const fd = new FormData();
  const name = `import_${DateTime.now().toFormat('yyyy-MM-dd')}.txt`;

  let newFile: File | Blob;
  try {
    newFile = new File([file], name, { type: 'text/plain' });
  } catch {
    newFile = new Blob([file], { type: 'text/plain' });
    (newFile as Blob & { name?: string }).name = name;
  }

  fd.append('file', newFile);
  fd.append('name', name);
  return fd;
};

/** ESB sends these without an offset, so they are read as local time */
export const formatImportDate = (value?: string | null): string | null => {
  if (!value) return null;
  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed.toFormat('MMM d, yyyy h:mm a') : null;
};

export const buildCampFormData = (
  file: File,
  options: ImportMBFileUploadOptions,
): FormData => {
  const fd = new FormData();
  fd.append('file', file);
  // The note is optional; an empty string may fail CoreAPI's validation
  const comment = options.comment.trim();
  if (comment) fd.append('comment', comment);
  fd.append('meritBadgeRequirements', String(options.meritBadgeRequirements));
  fd.append('completeMeritBadges', String(options.completeMeritBadges));
  fd.append(
    'leaderApproveMeritBadges',
    String(options.leaderApproveMeritBadges),
  );
  return fd;
};

export const extractImportErrors = (error: unknown): string[] => {
  const data = (
    error as {
      data?: { errorDesc?: unknown; statusText?: string };
      status?: unknown;
    }
  )?.data;

  const desc = data?.errorDesc;
  if (Array.isArray(desc)) return desc.map(String);
  if (desc) return [String(desc)];
  if (data?.statusText) return [data.statusText];
  return [];
};

export const isCampUnauthorized = (
  error: FetchBaseQueryError | SerializedError | undefined,
): boolean => !!error && 'status' in error && error.status === 403;

/**
 * `summaryReport` has also been observed returning an HTML table, so a parse
 * failure is expected rather than exceptional - callers just lose the counts
 */
export const parseCampSummary = (
  summaryReport?: string,
): CampSummary | null => {
  if (!summaryReport) return null;
  try {
    const parsed = JSON.parse(summaryReport);
    return typeof parsed === 'object' && parsed ? parsed : null;
  } catch {
    return null;
  }
};

export const toCampCount = (value?: string) => {
  if (value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * IMPORTANT:
 * The import is council-scoped, so another leader's job can be the latest one.
 * Only the uploader follows its progress; everyone else is locked out until it
 * finishes, and a finished foreign job counts as no import
 */
export const getCampOwnership = (
  status: GetImportMBFileResponse | undefined,
  userId: number | undefined,
) => {
  if (!status?.processStatus) return { isOwn: false, lockedByOther: false };
  const isOwn = userId !== undefined && status.adminUserId === userId;
  return {
    isOwn,
    lockedByOther: !isOwn && !isCampTerminal(status.processStatus),
  };
};

export const getImportButtonState = (
  campImport: Pick<CampImportStatus, 'view' | 'isWorking' | 'needsAction'>,
): ImportButtonState => {
  if (campImport.needsAction) return 'needsReview';
  if (campImport.isWorking) return 'working';
  if (campImport.view?.isDone) return 'done';
  if (campImport.view?.isError) return 'error';
  return 'idle';
};

/** The requirements endpoint also returns the rows that passed validation, with blank `errors` */
export const hasImportErrors = (row: ImportFileRequirementRow): boolean =>
  !!row.errors?.trim();

export const IMPORT_ERRORS_CSV_COLUMNS = [
  'Row',
  'Charter Name',
  'BSA Member ID',
  'First Name',
  'Middle Name',
  'Last Name',
  'Advancement Type',
  'Advancement',
  'MB Version',
  'Date Completed',
  'Approved',
  'Awarded',
  'MB ID',
  'MB Version ID',
  'MB Requirement ID',
  'User ID',
  'Record ID',
  'Errors',
];

const csvText = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value);

const csvBool = (value: boolean): string => (value ? 'Yes' : 'No');

export const buildImportErrorsCsv = (
  rows: ImportFileRequirementRow[],
): ImportErrorsCsvColumns[] =>
  rows.map(row => ({
    Row: row.rowNumber,
    'Charter Name': csvText(row.charterName),
    'BSA Member ID': csvText(row.bsaMemberId),
    'First Name': csvText(row.firstName),
    'Middle Name': csvText(row.middleName),
    'Last Name': csvText(row.lastName),
    'Advancement Type': csvText(row.advancementType),
    Advancement: csvText(row.advancement),
    'MB Version': csvText(row.mbVersion),
    'Date Completed': csvText(row.dateCompleted),
    Approved: csvBool(row.approved),
    Awarded: csvBool(row.awarded),
    'MB ID': csvText(row.mbId),
    'MB Version ID': csvText(row.mbVersionId),
    'MB Requirement ID': csvText(row.mbReqId),
    'User ID': csvText(row.userId),
    'Record ID': row.id,
    Errors: csvText(row.errors),
  }));

export const buildImportErrorsFilename = (source?: string | null): string => {
  const base = (source || 'import')
    .replace(/\.csv$/i, '')
    .replace(/[\\/]+/g, '-');
  return `import-errors_${base}_${DateTime.now().toFormat('yyyy-MM-dd')}`;
};

export const downloadImportErrorsCsv = (
  rows: ImportFileRequirementRow[],
  source?: string | null,
) => {
  const filename = buildImportErrorsFilename(source);
  const file = convertCSVColumnstoFile<ImportErrorsCsvColumns>({
    parsedCSV: buildImportErrorsCsv(rows),
    columns: IMPORT_ERRORS_CSV_COLUMNS,
    filename,
  });

  const link = document.createElement('a');
  link.href = URL.createObjectURL(file);
  link.download = `${filename}.csv`;
  link.click();

  URL.revokeObjectURL(link.href);
};
